'use client'

import { useCallback } from 'react'
import type { FormStateReturn } from './useFormState'
import type { UseFormSubmissionReturn } from './useFormSubmission'
import { toggleChecklistItem } from '@/entries'
import { isMediaKind, type MediaKind } from '@/entries'
import type { EnrichedMediaResult } from '@/enrichment'
import type { MediaType as EnrichmentMediaType } from '@/enrichment'
import type { AnyAnalysis } from '../analysis/useEntryAnalysis'
import type { CaptureEntryType } from '../types'
import type { JournalAnalysis, TaskAnalysis } from '../analysis/types'
import type { MediaAnalysis } from '../ingest/from-analysis'
import { useCreatePerson } from '@/entries'
import { useFindOrCreateTopic } from '@/topics'

interface PersonRef {
  id: string
  title: string
}

interface UseFormHandlersOptions {
  formState: FormStateReturn
  submission: UseFormSubmissionReturn
  analysis: AnyAnalysis | null
  aiDetectedType: string | null
  isMediaDetected: boolean
  enrichedData: EnrichedMediaResult | null
  effectiveType: CaptureEntryType | null
  selectedPeople: PersonRef[]
  resetAnalysis: () => void
  resetEnrichment: () => void
  setEnrichedData: (data: EnrichedMediaResult) => void
  onSuccess?: () => void
}

export interface FormHandlers {
  handleResetAll: () => void
  handleSubmit: (e: React.FormEvent) => Promise<void>
  handleMediaSubmit: () => Promise<void>
  handleMediaDismiss: () => void
  handleManualMediaSubmit: () => Promise<void>
  handleManualMediaCancel: () => void
  handleSelectAlternative: (alternative: {
    externalId: string
    name: string
    imageUrl?: string
    type?: string
    genres?: string[]
    year?: number
  }) => void
  handleAddPerson: (person: { id: string }) => void
  handleRemovePerson: (person: { id: string }) => void
  handleTopicsChange: (topicIds: string[]) => void
  handleCreateTopic: (name: string) => void
  handleChecklistToggle: (id: string, checked: boolean) => void
  handleAddSuggestedPerson: (name: string) => void
  handleDismissSuggestedPerson: (name: string) => void
}

export function useFormHandlers({
  formState,
  submission,
  analysis,
  aiDetectedType,
  isMediaDetected,
  enrichedData,
  effectiveType,
  selectedPeople,
  resetAnalysis,
  resetEnrichment,
  setEnrichedData,
  onSuccess,
}: UseFormHandlersOptions): FormHandlers {
  const createPerson = useCreatePerson()
  const findOrCreateTopic = useFindOrCreateTopic()

  const handleResetAll = useCallback(() => {
    formState.resetDraft()
    resetAnalysis()
    resetEnrichment()
    formState.setLastAppliedAnalysis(null)
    formState.setUserSelectedType(null)
    formState.setSuggestedPeople([])
    formState.setEditedMediaValues(undefined)
    formState.setShowManualMediaForm(false)
    formState.setDismissedMediaType(null)
    formState.setGenresInputValue('')
    formState.resetUserModified()
  }, [formState, resetAnalysis, resetEnrichment])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (isMediaDetected) return

      formState.setError(null)

      try {
        await submission.submitJournalOrTask(
          formState,
          effectiveType,
          selectedPeople,
          analysis as JournalAnalysis | TaskAnalysis | null,
          {
            onResetAll: handleResetAll,
            onSuccess,
          },
        )
      } catch (err) {
        formState.setError(
          err instanceof Error
            ? err.message
            : 'Could not save entry. Please try again.',
        )
      }
    },
    [
      isMediaDetected,
      formState,
      submission,
      effectiveType,
      selectedPeople,
      analysis,
      handleResetAll,
      onSuccess,
    ],
  )

  const handleMediaSubmit = useCallback(async () => {
    if (!isMediaDetected || !aiDetectedType || !analysis) return
    if (!isMediaKind(aiDetectedType)) return

    formState.setError(null)

    try {
      await submission.submitMedia(
        aiDetectedType as MediaKind,
        analysis as MediaAnalysis,
        enrichedData,
        formState.editedMediaValues,
        {
          onResetAll: handleResetAll,
          onSuccess,
        },
      )
    } catch (err) {
      formState.setError(
        err instanceof Error
          ? err.message
          : 'Could not save to library. Please try again.',
      )
    }
  }, [
    isMediaDetected,
    aiDetectedType,
    analysis,
    formState,
    submission,
    enrichedData,
    handleResetAll,
    onSuccess,
  ])

  const handleMediaDismiss = useCallback(() => {
    // Keep the detected type for manual entry
    if (aiDetectedType && isMediaKind(aiDetectedType)) {
      formState.setDismissedMediaType(aiDetectedType as MediaKind)
      formState.setShowManualMediaForm(true)
      // Initialize manual form values from analysis if available
      if (analysis && isMediaKind(aiDetectedType)) {
        const mediaAnalysis = analysis as MediaAnalysis
        const initialValues: Record<string, unknown> = {}
        if ('title' in mediaAnalysis && mediaAnalysis.title) {
          initialValues.title = mediaAnalysis.title
        } else if ('name' in mediaAnalysis && mediaAnalysis.name) {
          initialValues.name = mediaAnalysis.name
        }
        if ('genres' in mediaAnalysis && mediaAnalysis.genres) {
          initialValues.genres = mediaAnalysis.genres
          formState.setGenresInputValue(mediaAnalysis.genres.join(', '))
        } else {
          formState.setGenresInputValue('')
        }
        if ('year' in mediaAnalysis && mediaAnalysis.year) {
          initialValues.year = mediaAnalysis.year
        }
        if ('director' in mediaAnalysis) {
          const director = mediaAnalysis.director
          if (director && typeof director === 'string') {
            initialValues.director = director
          }
        }
        if ('author' in mediaAnalysis) {
          const author = mediaAnalysis.author
          if (author && typeof author === 'string') {
            initialValues.author = author
          }
        }
        formState.setEditedMediaValues(initialValues)
      } else {
        formState.setGenresInputValue('')
      }
    }
    resetAnalysis()
    resetEnrichment()
  }, [aiDetectedType, analysis, formState, resetAnalysis, resetEnrichment])

  const handleManualMediaSubmit = useCallback(async () => {
    if (!formState.dismissedMediaType || !formState.content.trim()) return

    formState.setError(null)

    try {
      await submission.submitManualMedia(
        formState.dismissedMediaType,
        formState.content,
        formState.editedMediaValues,
        {
          onResetAll: handleResetAll,
          onSuccess,
        },
      )
    } catch (err) {
      formState.setError(
        err instanceof Error
          ? err.message
          : 'Could not save to library. Please try again.',
      )
    }
  }, [formState, submission, handleResetAll, onSuccess])

  const handleManualMediaCancel = useCallback(() => {
    formState.setShowManualMediaForm(false)
    formState.setDismissedMediaType(null)
    formState.setEditedMediaValues(undefined)
    formState.setGenresInputValue('')
  }, [formState])

  const handleSelectAlternative = useCallback(
    (alternative: {
      externalId: string
      name: string
      imageUrl?: string
      type?: string
      genres?: string[]
      year?: number
    }) => {
      if (!enrichedData) return

      const selectedType =
        (alternative.type as EnrichmentMediaType) || enrichedData.type

      setEnrichedData({
        ...enrichedData,
        type: selectedType,
        enriched: {
          externalId: alternative.externalId,
          title: alternative.name,
          imageUrl: alternative.imageUrl,
          genres: alternative.genres,
          year: alternative.year,
        },
        alternatives: enrichedData.enriched
          ? [
              {
                externalId: enrichedData.enriched.externalId || '',
                name: enrichedData.enriched.title || enrichedData.name,
                imageUrl: enrichedData.enriched.imageUrl,
                type: enrichedData.type,
                genres: enrichedData.enriched.genres,
                year: enrichedData.enriched.year,
              },
            ]
          : [],
        confidence: 'high',
      })
    },
    [enrichedData, setEnrichedData],
  )

  const handleAddPerson = useCallback(
    (person: { id: string }) => {
      formState.markFieldModified('people')
      if (!formState.selectedPeopleIds.includes(person.id)) {
        formState.setDraftValue('selectedPeopleIds', [
          ...formState.selectedPeopleIds,
          person.id,
        ])
      }
    },
    [formState],
  )

  const handleRemovePerson = useCallback(
    (person: { id: string }) => {
      formState.markFieldModified('people')
      formState.setDraftValue(
        'selectedPeopleIds',
        formState.selectedPeopleIds.filter((id) => id !== person.id),
      )
    },
    [formState],
  )

  const handleTopicsChange = useCallback(
    (topicIds: string[]) => {
      formState.markFieldModified('topics')
      formState.setDraftValue('selectedTopicIds', topicIds)
    },
    [formState],
  )

  const handleCreateTopic = useCallback(
    (name: string) => {
      const topicId = findOrCreateTopic(name, effectiveType)
      if (topicId && !formState.selectedTopicIds.includes(topicId)) {
        formState.markFieldModified('topics')
        formState.setDraftValue('selectedTopicIds', [
          ...formState.selectedTopicIds,
          topicId,
        ])
      }
    },
    [findOrCreateTopic, effectiveType, formState],
  )

  const handleChecklistToggle = useCallback(
    (id: string, checked: boolean) => {
      formState.markFieldModified('checklist')
      if (formState.checklistItems) {
        formState.setDraftValue(
          'checklistItems',
          toggleChecklistItem(formState.checklistItems, id, checked),
        )
      }
    },
    [formState],
  )

  const handleAddSuggestedPerson = useCallback(
    (name: string) => {
      formState.setAddingPerson(name)
      createPerson.mutate(
        { name, category: 'friend', birthday: null, notes: null },
        {
          onSuccess: (newPerson) => {
            formState.setSuggestedPeople((prev) =>
              prev.filter((n) => n !== name),
            )

            if (newPerson?.id) {
              formState.markFieldModified('people')
              formState.setDraftValue('selectedPeopleIds', [
                ...formState.selectedPeopleIds,
                newPerson.id,
              ])
            }
            formState.setAddingPerson(null)
          },
          onError: (err) => {
            console.error('Failed to add person:', err)
            formState.setAddingPerson(null)
          },
        },
      )
    },
    [formState, createPerson],
  )

  const handleDismissSuggestedPerson = useCallback(
    (name: string) => {
      formState.setSuggestedPeople((prev) => prev.filter((n) => n !== name))
    },
    [formState],
  )

  return {
    handleResetAll,
    handleSubmit,
    handleMediaSubmit,
    handleMediaDismiss,
    handleManualMediaSubmit,
    handleManualMediaCancel,
    handleSelectAlternative,
    handleAddPerson,
    handleRemovePerson,
    handleTopicsChange,
    handleCreateTopic,
    handleChecklistToggle,
    handleAddSuggestedPerson,
    handleDismissSuggestedPerson,
  }
}
